import type { ReadonlySignal } from '@preact/signals-react';
import type { InventoryChangeKind, SymbolPricesResponse } from '@thinkalpha/locates-ws-client';
import type {
    BorrowStatusKey,
    PoolPricingConfig,
    PoolPricingMethod,
} from '@thinkalpha/ui-types/types/admin/lab-pool-pricing';
import type { PricingResolution } from 'src/admin/models/PoolManagementModel/pricing/derivePricingResolution';
import type {
    ChangeLogActionFilter,
    ChangeLogTimeRange,
    CreatePoolDraft,
    InventoryRowsPerPage,
    PoolDetailTab,
    PoolStatusFilter,
    UploadApplyProgress,
    UploadMode,
    UploadStreamError,
} from 'src/admin/models/PoolManagementModel/state';
import type { UploadDiff } from 'src/admin/models/PoolManagementModel/upload/diffInventory';
import type { InventoryParseResult } from 'src/admin/models/PoolManagementModel/upload/parseInventoryCsv';

export { isDraftRowId, MIN_DELETE_POOL_REASON_LENGTH } from 'src/admin/models/PoolManagementModel/impl';
export type {
    PricingResolution,
    PricingResolutionMinimum,
    PricingResolutionRow,
} from 'src/admin/models/PoolManagementModel/pricing/derivePricingResolution';
export type {
    ChangeLogActionFilter,
    ChangeLogTimeRange,
    CreatePoolDraft,
    CreatePoolInitMode,
    InventoryRowsPerPage,
    PoolDetailTab,
    PoolStatusFilter,
    UploadApplyProgress,
    UploadMode,
    UploadStreamError,
} from 'src/admin/models/PoolManagementModel/state';

/**
 * One row in the Change Log tab. Mirrors the flat `ChangeLogEntry`
 * wire contract from `@thinkalpha/locates-ws-client`: the change `kind`
 * determines which of `quantity` / `price` / `available` carry the
 * change's own figures (the others are `null` — no before/after in the
 * contract).
 */
export interface InventoryChangeEntry {
    id: string;
    /** ms since epoch. */
    timestamp: number;
    /** The user that authored the change; `null` for automated processes. */
    userId: string | null;
    /** Display label. Falls back to "System" when {@link userId} is null. */
    userName: string;
    /** Originating locate-order id, when the change came from a locate. */
    orderId: string | null;
    /** The kind of change; keys the row's label, colour, and detail text. */
    kind: InventoryChangeKind;
    symbol: string;
    /** Signed delta / initial / accepted quantity, per {@link kind}; `null` when unaffected. */
    quantity: number | null;
    /** New / initial / accepted price, per {@link kind}; `null` when unaffected. */
    price: number | null;
    /** New availability for an availability change; `null` for every other kind. */
    available: boolean | null;
}

export type PoolManagementView = 'list' | 'detail';

export type PoolPermission = 'reader' | 'writer' | 'owner';

/**
 * One row in the Permissions tab. The tab is scoped to firm members
 * only: even though the backend can store permissions for groups,
 * firms, or special pseudo-subjects, the UI explicitly restricts grants
 * (and the holder list) to individual users that belong to the pool's
 * firm. `userName` / `email` come from the firm-members directory; any
 * holder that doesn't resolve to a firm member is dropped from the view.
 */
export interface PoolPermissionHolder {
    /** UUID of the user — never shown directly. */
    userId: string;
    userName: string;
    email: string;
    /** The highest rung this user currently holds on the pool. */
    role: PermissionRole;
}

/** A firm-member candidate the grant form can offer. */
export interface PoolPermissionCandidate {
    userId: string;
    userName: string;
    email: string;
}

/**
 * The four-rung permission ladder, in UI vocabulary. Matches the
 * locates-ws `PermissionType` exactly so the wire mapping is a
 * pass-through.
 */
export type PermissionRole = 'reader' | 'writer' | 'owner' | 'admin';

/**
 * Active = routing is on. Paused = routing is suspended; in-flight
 * orders complete, no new ones are sent. Derived from
 * `InventoryPool.active`: `'active'` when `true`, `'paused'` when
 * `false`. The richer UI enum gives room for additional statuses
 * (e.g. `'draining'`) without re-keying templates if the backend
 * widens the field later.
 */
export type PoolStatus = 'active' | 'paused';

/**
 * Per-row borrow status the firm has on a symbol, as emitted by the
 * locates-ws inventory endpoint. The backend types this as `string`
 * but the only values produced today are `'Locate Required' |
 * 'Preborrow' | 'ETB'`, sourced from the Wedbush SLL. We re-declare
 * the union here so view code can render labelled buckets while
 * still tolerating any unrecognized value via a `string` fallback.
 */
export type BorrowStatus = 'Locate Required' | 'Preborrow' | 'ETB' | (string & {});

export interface Pool {
    id: string;
    name: string;
    description: string | null;
    /** UUID of the firm that owns this pool. */
    firmId: string;
    /** Notification email for pool events (e.g. inventory-loaded summaries). */
    eventEmail: string;
    permission: PoolPermission;
    status: PoolStatus;
    /** The pool's persisted pricing config; `null` until an owner configures one. */
    pricing: PoolPricingConfig | null;
}

/**
 * The pool's editable info fields (PLAT-12970). `description` uses `''`
 * for "unset" so the textarea can bind directly; `eventEmail` is
 * required by the backend, so for it `''` is just the empty-input state
 * and fails {@link PoolManagementModel.isPoolInfoDraftValid}.
 */
export interface PoolInfoDraft {
    name: string;
    description: string;
    eventEmail: string;
}

/**
 * Mirrors the backend's notification-email validation for
 * `PATCH /v1/inventory/pools/{poolId}`, so a 400 is never the first
 * feedback. Shared by the model's draft validity and the Info tab's
 * per-field hint.
 */
export const EVENT_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface InventoryRow {
    id: string;
    symbol: string;
    currentQuantity: number;
    currentLabPrice: number;
    /**
     * The firm's current borrow status for the symbol, from the locates-
     * ws inventory endpoint (sourced from the Wedbush SLL). `null` when
     * no Wedbush client is configured for the pool's firm or the symbol
     * is not in the SLL. UI renders `—` for `null`.
     */
    borrowStatus: BorrowStatus | null;
    /**
     * Shares of the row already consumed by routed orders today.
     * Sourced from the inlined `quantityUsed` on the inventory item;
     * `null` when the backend hasn't recorded any usage for the symbol.
     * UI renders `—` for `null`.
     */
    quantityUsed: number | null;
    /**
     * Revenue this row has produced today, in USD. `null` until the
     * backend tracks revenue against inventory items. UI renders `—`
     * for `null`.
     */
    dailyRevenue: number | null;
    /**
     * Whether the position is published (`true`, eligible to fill
     * locates) or suspended from the list (`false`). Toggled via
     * `setItemAvailability`; modern pools only — the legacy backend has
     * no availability concept and always reports `true`.
     */
    available: boolean;
    /** Reference auction floor price. `null` when unavailable; UI renders `—`. */
    currentPrice: number | null;
    /** ifeed-extended fair value. Always `null` until PLAT-12560 ships; UI renders `—`. */
    fairValuePrice: number | null;
}

export interface InventoryRowEdit {
    rowId: string;
    currentQuantity?: number;
    currentLabPrice?: number;
}

/**
 * State of the per-row pricing-resolution sidecard (PLAT-12936). The row's
 * symbol and borrow status render from the row itself; this carries only
 * the simulation lifecycle for the hovered row.
 */
export type PricingResolutionCardState = { rowId: string } & (
    | { status: 'loading' }
    | { status: 'error' }
    /** The pool has no pricing configured (the backend's 400) — expected for unconfigured pools. */
    | { status: 'not-configured' }
    | { status: 'ready'; resolution: PricingResolution }
);

/**
 * State of the Info tab's pricing simulator (PLAT-12937). `symbol` is the
 * normalized (trimmed, upper-cased) symbol the run was started for.
 * `borrowStatus` is the server-resolved status echoed on the simulation
 * response — it contextualizes the minimum-price row.
 * `requires-saved-config` means the pool has no persisted config, so there
 * is no reader-scoped source for candidate prices and only the
 * superuser-only arbitrary-config simulation could preview — the card
 * shows a hint instead of firing a request that can only 403. Unsaved
 * edits on a configured pool preview fine: the saved-config simulation
 * supplies the market inputs and the draft re-resolves locally.
 */
export type PricingSimulatorState = { symbol: string } & (
    | { status: 'loading' }
    | { status: 'error' }
    | { status: 'requires-saved-config' }
    | { status: 'ready'; resolution: PricingResolution; borrowStatus: BorrowStatus | null }
);

/**
 * State of the read-only price summary dialog (PLAT-12938): the
 * locate-price index for a symbol — every price level with its
 * contributing providers — as opposed to the sidecard's config-driven
 * resolution.
 */
export type PriceSummaryState = { symbol: string } & (
    | { status: 'loading' }
    | { status: 'error' }
    | {
          status: 'ready';
          prices: SymbolPricesResponse;
          /**
           * Provider display names keyed by id, resolved best-effort from
           * the firm's provider list — the index itself identifies
           * providers by id alone. Ids without an entry render as-is.
           */
          providerNames: Readonly<Record<string, string>>;
      }
);

export interface PoolManagementModel extends Disposable {
    // --- Firm / loading state ---
    // The page firm display name (subtitle) is still presentation-only.
    // The `firmId → firmName` map is on the model because search filters
    // on it and the detail view reads through it.
    readonly isLoading: ReadonlySignal<boolean>;
    readonly error: ReadonlySignal<string | null>;

    // --- Pool list ---
    readonly pools: ReadonlySignal<ReadonlyArray<Pool>>;
    readonly poolSearchQuery: ReadonlySignal<string>;
    readonly poolStatusFilter: ReadonlySignal<PoolStatusFilter>;
    /** Pools after applying both the search query and the status filter. */
    readonly filteredPools: ReadonlySignal<ReadonlyArray<Pool>>;
    /**
     * `firmId → firmName` lookup. Non-null only in the superuser scope
     * (firmId === null). The list view uses it to render per-card firm
     * names and to extend the search filter; the detail view uses it via
     * {@link selectedPoolFirmName}.
     */
    readonly firmNamesByFirmId: ReadonlySignal<Readonly<Record<string, string>> | null>;

    // --- View / selection ---
    readonly view: ReadonlySignal<PoolManagementView>;
    /** Pool id when the detail view is open, otherwise null. Available before the pool list query resolves (unlike `selectedPool`). */
    readonly selectedPoolId: ReadonlySignal<string | null>;
    readonly selectedPool: ReadonlySignal<Pool | null>;
    readonly selectedPoolPermission: ReadonlySignal<PoolPermission | null>;
    /**
     * Owning firm's display name for the currently-open pool, resolved
     * through {@link firmNamesByFirmId}. Null when no pool is open, when
     * the map isn't populated yet, or in the firm-scoped view (where the
     * firm is already in the page subtitle).
     */
    readonly selectedPoolFirmName: ReadonlySignal<string | null>;
    /**
     * Whether the open pool is a legacy (SQL-backed) pool per the
     * `legacy-lab-pools` feature flag, whose payload lists the legacy pool
     * ids. Views hide or disable modern-pool-only features when this is
     * true (PLAT-12869). `false` when no pool is open.
     */
    readonly selectedPoolIsLegacy: ReadonlySignal<boolean>;
    /**
     * Ids of legacy pools per the `legacy-lab-pools` feature flag, for
     * per-pool gates in list contexts (e.g. hiding a pool card's Delete
     * action, which the backend rejects for legacy pools).
     */
    readonly legacyPoolIds: ReadonlySignal<ReadonlySet<string>>;

    // --- Permission gates for the selected pool ---
    readonly canRead: ReadonlySignal<boolean>;
    readonly canWrite: ReadonlySignal<boolean>;
    readonly canOwn: ReadonlySignal<boolean>;

    // --- Inventory rows for the selected pool ---
    readonly serverInventoryRows: ReadonlySignal<ReadonlyArray<InventoryRow>>;
    readonly displayedInventoryRows: ReadonlySignal<ReadonlyArray<InventoryRow>>;
    /**
     * Server-only rows with the current filter applied. Excludes
     * pending draft additions; those are exposed separately via
     * {@link pendingDraftRows} so the table can keep them visible
     * regardless of filter / page state.
     */
    readonly filteredInventoryRows: ReadonlySignal<ReadonlyArray<InventoryRow>>;
    /**
     * Rows visible on the current page after filter + pagination.
     * Server-only — drafts render separately. The inventory table
     * renders from this.
     */
    readonly paginatedInventoryRows: ReadonlySignal<ReadonlyArray<InventoryRow>>;
    /**
     * Pending draft adds with their staged field edits applied. The
     * inventory table renders these in a separate section above the
     * paginated body so they stay visible regardless of the current
     * page, filter, or page size.
     */
    readonly pendingDraftRows: ReadonlySignal<ReadonlyArray<InventoryRow>>;
    /**
     * Every server row of the selected pool, independent of table
     * pagination. Only populated while the upload wizard or the
     * submit-review dialog is open (the backing full-pool query is
     * disabled otherwise); `null` while disabled or loading. The
     * submit-review dialog reads through this so changes staged on
     * other pages still render.
     */
    readonly fullInventoryRows: ReadonlySignal<ReadonlyArray<InventoryRow> | null>;
    /**
     * Error message from the full-pool snapshot fetch; `null` while the
     * query is disabled, loading, or succeeded. Lets consumers of
     * {@link fullInventoryRows} distinguish a failed fetch from a
     * loading one (both leave the rows `null`).
     */
    readonly fullInventorySnapshotError: ReadonlySignal<string | null>;
    /**
     * Sum of `dailyRevenue` across every server-known row in the
     * selected pool, in USD. `null` when no row in the pool reports
     * a value (which is the phase-2 default until backend support
     * lands). The pool detail toolbar renders this as the "TOTAL
     * DAILY" indicator.
     */
    readonly totalDailyRevenue: ReadonlySignal<number | null>;
    readonly inventoryPage: ReadonlySignal<number>;
    readonly inventoryRowsPerPage: ReadonlySignal<InventoryRowsPerPage>;
    /** Clamped to 1+ even when there are no rows so the page UI is always navigable. */
    readonly inventoryPageCount: ReadonlySignal<number>;
    /**
     * Server-reported total row count for the current pool, used to
     * drive the "showing X-Y of N" label and the page stepper. Does
     * not include pending local adds — those render separately above
     * the paginated table.
     */
    readonly inventoryTotalCount: ReadonlySignal<number>;
    setInventoryPage(page: number): void;
    setInventoryRowsPerPage(rowsPerPage: InventoryRowsPerPage): void;
    readonly rowEdits: ReadonlySignal<Readonly<Record<string, InventoryRowEdit>>>;
    readonly pendingRowRemovals: ReadonlySignal<ReadonlySet<string>>;

    // --- Detail-view editing state ---
    readonly symbolSearchQuery: ReadonlySignal<string>;
    readonly addSymbolDraft: ReadonlySignal<string>;
    readonly isDirty: ReadonlySignal<boolean>;
    /**
     * `true` when anything on the open pool would be lost by navigating
     * away: staged row edits ({@link isDirty}), an unsaved pricing
     * config draft ({@link isPricingConfigDirty}), or an unsaved pool
     * info draft ({@link isPoolInfoDirty}). Drives the discard prompts
     * and the route blocker; the submit bar stays on {@link isDirty}
     * because it only submits row-level changes.
     */
    readonly hasUnsavedChanges: ReadonlySignal<boolean>;
    /**
     * Number of row-level changes staged for submit: draft additions,
     * edits to existing rows, and pending removals. A draft row with
     * edits on it counts once (the addition is the operation); an
     * edited-then-removed row counts once (the removal supersedes).
     * `addSymbolDraft` text is unsaved but not yet a change, so it is
     * excluded.
     */
    readonly unsavedChangeCount: ReadonlySignal<number>;
    readonly submitReviewOpen: ReadonlySignal<boolean>;
    readonly isSubmitting: ReadonlySignal<boolean>;
    readonly submitError: ReadonlySignal<string | null>;
    /** True while {@link downloadCurrentPoolCsv} is fetching and building the export. */
    readonly isDownloadingCsv: ReadonlySignal<boolean>;

    // --- Upload wizard ---
    readonly uploadWizardOpen: ReadonlySignal<boolean>;
    readonly uploadStep: ReadonlySignal<'drop' | 'review' | 'confirm' | 'done'>;
    readonly uploadFile: ReadonlySignal<{ name: string; size: number; uploadedAt: number } | null>;
    readonly uploadParseResult: ReadonlySignal<InventoryParseResult | null>;
    readonly uploadDiff: ReadonlySignal<UploadDiff | null>;
    readonly uploadCategoryFilter: ReadonlySignal<'added' | 'updated' | 'removed' | 'errors' | 'unchanged'>;
    readonly uploadSymbolFilter: ReadonlySignal<string>;
    /**
     * How the upload diff is applied. Pinned to `'merge'` in phase 1
     * (no UI to flip it yet); the type leaves room for `'replace'`
     * and other future modes without re-plumbing.
     */
    readonly uploadMode: ReadonlySignal<UploadMode>;
    readonly uploadApplying: ReadonlySignal<boolean>;
    /** Live row progress while an apply is running; null when idle. */
    readonly uploadProgress: ReadonlySignal<UploadApplyProgress | null>;
    /**
     * Failures the backend streamed for the current upload apply
     * (PLAT-12803), in arrival order — the wizard's messages panel.
     * Empty until an error arrives; cleared when the wizard opens/closes
     * or a new apply starts.
     */
    readonly uploadStreamErrors: ReadonlySignal<ReadonlyArray<UploadStreamError>>;
    readonly uploadError: ReadonlySignal<string | null>;
    readonly uploadAppliedSummary: ReadonlySignal<{
        added: number;
        updated: number;
        removed: number;
        rejected: number;
    } | null>;

    // --- Scope ---
    /**
     * Set the firm scope for the pool list. Pass `null` for superuser
     * "list all pools" mode; pass a firm id for the firm-scoped view.
     * Resets the open pool and any in-flight edits when the scope
     * changes — when the current pool has unsaved changes the call is
     * staged behind {@link discardChangesOpen} and the user must
     * confirm via {@link confirmDiscardChanges} before edits are
     * dropped.
     */
    setFirm(firmId: string | null): void;

    /**
     * Provide the `firmId → firmName` lookup for the superuser view, or
     * `null` to clear it (firm-scoped view doesn't need it). Driven from
     * the React shim's firms query.
     */
    setFirmNames(map: Readonly<Record<string, string>> | null): void;

    /**
     * Provide the scoped firm's display name in the firm-scoped view
     * (where {@link firmNamesByFirmId} is null), or `null` to clear it.
     * Used by the pool list CSV export's firm column.
     */
    setFirmName(name: string | null): void;

    // --- Pool detail tabs ---
    /**
     * Currently-selected tab inside the pool detail view. The stored
     * value defaults to `info`, and {@link canManagePermissions} gates
     * whether the Permissions tab is actually visible — when it is
     * `false`, this signal coerces a stored `permissions` value to
     * `info` on read, so non-superusers never land on the hidden tab.
     * Use {@link setActivePoolDetailTab} to change it.
     */
    readonly activePoolDetailTab: ReadonlySignal<PoolDetailTab>;
    setActivePoolDetailTab(tab: PoolDetailTab): void;

    /**
     * `true` when the caller has session-level superuser access (which
     * gates the Permissions tab). Derived directly from
     * `CurrentUserModel.isSuperuser` and `isSuperUserModeEnabled` so
     * the model picks up superuser toggles automatically — no React-
     * side wiring required.
     */
    readonly canManagePermissions: ReadonlySignal<boolean>;

    // --- Pricing resolution sidecard (PLAT-12936) ---
    /**
     * `true` when the caller may open the per-row pricing-resolution
     * sidecard: the backing item-pricing endpoint requires `reader` on
     * the pool — the same access that shows the inventory table.
     */
    readonly canViewPricingResolution: ReadonlySignal<boolean>;
    /**
     * State of the pricing-resolution sidecard; `null` while none is
     * open. Only one sidecard is active at a time (single pointer).
     * Simulations are cached in the query cache, so re-hovering a
     * recently simulated row opens `ready` immediately instead of
     * flashing the loader.
     */
    readonly pricingResolutionCard: ReadonlySignal<PricingResolutionCardState | null>;
    /**
     * Open the sidecard for a server row and load its active pricing
     * from the pool's item-pricing endpoint (the item supplies the
     * symbol and stored price; the row's borrow status selects the
     * configured floor). No-op for draft rows (no server item yet),
     * when the row is gone, or when the caller lacks
     * {@link canViewPricingResolution}.
     */
    showPricingResolution(rowId: string): void;
    /**
     * Close the sidecard. No-op unless `rowId` matches the currently
     * open card, so a lagging teardown from a previous row cannot clear
     * the card the pointer just opened on another row.
     */
    hidePricingResolution(rowId: string): void;

    // --- Pool pricing configuration (PLAT-12937) ---
    /**
     * `true` when the caller may edit and submit the pool's pricing
     * config — the pool PATCH requires `owner`, which is also what firm
     * admins hold on their firm's pools.
     */
    readonly canEditPricingConfig: ReadonlySignal<boolean>;
    /**
     * The pool's persisted pricing config as last seen from the server
     * (the default config while the pool has none yet).
     */
    readonly poolPricingSavedConfig: ReadonlySignal<PoolPricingConfig>;
    /**
     * The Info tab's editable pricing config. Diverges from
     * {@link poolPricingSavedConfig} while the admin reorders the
     * hierarchy or edits the minimum price; the simulator card previews
     * with this draft. Reset to the saved config on pool switch or
     * {@link discardPricingConfigChanges}.
     */
    readonly poolPricingDraftConfig: ReadonlySignal<PoolPricingConfig>;
    readonly isPricingConfigDirty: ReadonlySignal<boolean>;
    /** Replace the draft hierarchy; `hierarchy` must be a permutation of the current one. */
    setPricingHierarchy(hierarchy: PoolPricingMethod[]): void;
    /**
     * Toggle the draft's minimum-price floors. Disabling preserves the
     * per-status prices so re-enabling restores them.
     */
    setMinimumPriceEnabled(enabled: boolean): void;
    /**
     * Set (or clear, with `undefined`) the draft's price floor for one
     * borrow status; a status without a floor is unfloored.
     */
    setMinimumPriceForStatus(status: BorrowStatusKey, price: number | undefined): void;
    discardPricingConfigChanges(): void;
    /** `true` while the submit-pricing confirm dialog is open. */
    readonly isPricingConfirmOpen: ReadonlySignal<boolean>;
    requestSubmitPricingConfig(): void;
    cancelSubmitPricingConfig(): void;
    /** `true` while the pricing PATCH is in flight; the confirm button disables. */
    readonly isSavingPricingConfig: ReadonlySignal<boolean>;
    /**
     * PATCH the draft onto the pool as its persisted pricing config,
     * toast the outcome, and reconcile the baseline against the server's
     * echo. The sidecard and simulator pick the new config up via the
     * pool refetch.
     */
    confirmSubmitPricingConfig(): void;

    // --- Pool info editing (PLAT-12970) ---
    /**
     * `true` when the caller may edit the pool's name, description, and
     * notification email — the pool PATCH requires `owner`, same as the
     * pricing config.
     */
    readonly canEditPoolInfo: ReadonlySignal<boolean>;
    /** The pool's info fields as last seen from the server. */
    readonly poolInfoSaved: ReadonlySignal<PoolInfoDraft>;
    /**
     * The Info tab's editable name / description / notification-email
     * draft. Reset to the saved copy on pool switch or
     * {@link discardPoolInfoChanges}.
     */
    readonly poolInfoDraft: ReadonlySignal<PoolInfoDraft>;
    readonly isPoolInfoDirty: ReadonlySignal<boolean>;
    /**
     * `false` while the draft can't be submitted — blank name or
     * malformed notification email — mirroring the backend's PATCH
     * validation so a 400 is never the first feedback.
     */
    readonly isPoolInfoDraftValid: ReadonlySignal<boolean>;
    setPoolInfoField(field: keyof PoolInfoDraft, value: string): void;
    discardPoolInfoChanges(): void;
    /** `true` while the info PATCH is in flight; the submit button disables. */
    readonly isSavingPoolInfo: ReadonlySignal<boolean>;
    /**
     * PATCH the changed info fields onto the pool, toast the outcome,
     * and reconcile the baseline against the server's echo.
     */
    submitPoolInfo(): void;

    // --- Price summary dialog (PLAT-12938) ---
    /**
     * `true` when the caller may open the price summary. Its backing
     * endpoint stays behind the backend's price-index capability
     * (superuser or a dedicated access role the UI cannot see), so only
     * superuser sessions get the trigger.
     */
    readonly canViewPriceSummary: ReadonlySignal<boolean>;
    /**
     * State of the price summary dialog; `null` while closed. Opened per
     * inventory row; the backing query caches per symbol, so re-opening
     * within the stale window shows `ready` without a loading flash.
     * Gate the trigger on {@link canViewPriceSummary}; failures
     * (including rollout 403s) surface as error toasts.
     */
    readonly priceSummary: ReadonlySignal<PriceSummaryState | null>;
    /** Open the dialog for a displayed row (server or draft) and load its price levels. */
    openPriceSummary(rowId: string): void;
    closePriceSummary(): void;

    // --- Pricing simulator (PLAT-12937) ---
    /** Raw simulator input as typed; normalization happens per run. */
    readonly simulatorSymbol: ReadonlySignal<string>;
    setSimulatorSymbol(value: string): void;
    /**
     * Latest simulation for the Info tab's simulator card; `null` while
     * the input is empty. Re-runs (debounced) whenever the symbol or the
     * draft config changes, so hierarchy edits preview live. A re-run
     * for an already-shown symbol keeps the previous result on screen
     * instead of flashing the loader.
     */
    readonly simulatorState: ReadonlySignal<PricingSimulatorState | null>;

    /**
     * Wall-clock ms of the most recent point at which the open pool's
     * view became fresh: a manual {@link refresh} settled, or a
     * successful mutation (submit, upload, pool status toggle,
     * permissions change) finished invalidating its queries. Drives
     * the toolbar's "Last refresh" stamp so it auto-resets after every
     * pool change without the view tracking mutations directly.
     */
    readonly lastRefreshAt: ReadonlySignal<number>;

    // --- Change Log tab ---
    /** Filtered + sorted entries for the Change Log tab, sourced from `GET /pools/{id}/log`. */
    readonly changeLogEntries: ReadonlySignal<ReadonlyArray<InventoryChangeEntry>>;
    /**
     * `true` while the change-log query is fetching its first page; the
     * tab shows a loading placeholder so the empty-state CTA doesn't
     * flash during the gap between activating the tab and the response
     * arriving.
     */
    readonly changeLogLoading: ReadonlySignal<boolean>;
    readonly changeLogSymbolFilter: ReadonlySignal<string>;
    /**
     * Action filter options for the open pool: the full change-kind
     * enumeration for modern pools; just `'all'` for legacy pools (see
     * {@link selectedPoolIsLegacy}), whose journal can't distinguish
     * kinds — the view hides the dropdown when there is nothing to choose.
     */
    readonly changeLogActionFilterValues: ReadonlySignal<readonly ChangeLogActionFilter[]>;
    /**
     * Active action filter after per-pool coercion — a stored kind that
     * {@link changeLogActionFilterValues} doesn't offer reads as `'all'`.
     */
    readonly changeLogActionFilter: ReadonlySignal<ChangeLogActionFilter>;
    readonly changeLogTimeRange: ReadonlySignal<ChangeLogTimeRange>;
    setChangeLogSymbolFilter(query: string): void;
    setChangeLogActionFilter(filter: ChangeLogActionFilter): void;
    setChangeLogTimeRange(range: ChangeLogTimeRange): void;
    /**
     * Server-reported total change-log entry count for the open pool.
     * Drives the "showing X-Y of N" label and page stepper. Client-side
     * filters do not narrow it — the backend log endpoint pages with
     * `skip` / `limit` only.
     */
    readonly changeLogTotalCount: ReadonlySignal<number>;
    readonly changeLogRowsPerPage: ReadonlySignal<InventoryRowsPerPage>;
    /** Clamped to 1+ even when there are no entries so the page UI is always navigable. */
    readonly changeLogPageCount: ReadonlySignal<number>;
    /** Clamped current page in `[1, changeLogPageCount]`. */
    readonly changeLogPage: ReadonlySignal<number>;
    setChangeLogPage(page: number): void;
    setChangeLogRowsPerPage(rowsPerPage: InventoryRowsPerPage): void;

    // --- Navigation commands ---
    /**
     * Open the pool list. If the current detail view has unsaved
     * changes ({@link isDirty}), the navigation is staged and
     * {@link discardChangesOpen} flips to `true` so the view can prompt
     * the user. {@link confirmDiscardChanges} commits the stashed nav
     * after wiping edits; {@link cancelDiscardChanges} drops it.
     */
    openPoolList(): void;
    /**
     * Open the detail view for `poolId`. When the open pool already has
     * unsaved changes, behaves like {@link openPoolList}: the target
     * `poolId` is stashed behind the discard-confirmation gate so the
     * user has a chance to keep their work.
     */
    openPoolDetail(poolId: string): void;

    // --- Unsaved-changes guard ---
    /**
     * `true` while the model is holding a navigation request behind the
     * unsaved-changes confirm. The view renders the confirm dialog when
     * this is on. The pending action itself is private — the view only
     * needs to confirm or cancel; the model performs the deferred work.
     */
    readonly discardChangesOpen: ReadonlySignal<boolean>;
    /** Commit the stashed navigation: clears edits and runs the deferred command. */
    confirmDiscardChanges(): void;
    /**
     * Drop the stashed navigation; the user keeps editing the current
     * pool. Any `onCancel` registered via {@link requestDiscardChanges}
     * is fired so external blockers (e.g. the React Router blocker
     * watching for in-app link clicks) can reset themselves.
     */
    cancelDiscardChanges(): void;
    /**
     * External-navigation entry point: when there are no unsaved edits,
     * runs `proceed` immediately. Otherwise stashes both callbacks
     * behind the confirm dialog. The React shim's route blocker calls
     * this with `blocker.proceed` / `blocker.reset` so off-page link
     * clicks share the in-app confirm UX.
     */
    requestDiscardChanges(proceed: () => void, onCancel?: () => void): void;

    // --- Create pool ---
    /** True while the Create Pool dialog is open. */
    readonly createPoolOpen: ReadonlySignal<boolean>;
    /** The dialog's form values. Reset every time the dialog opens. */
    readonly createPoolDraft: ReadonlySignal<CreatePoolDraft>;
    /**
     * `true` in the superuser "all firms" scope, where the draft must
     * name a target firm (the dialog renders a firm selector). `false`
     * in the firm-scoped view, where the page's firm is the target.
     */
    readonly createPoolNeedsFirm: ReadonlySignal<boolean>;
    /**
     * Whether the draft is submittable: name present, event email
     * shaped like an email, a firm picked when {@link createPoolNeedsFirm},
     * and no POST already in flight.
     */
    readonly canSubmitCreatePool: ReadonlySignal<boolean>;
    /** True while the create-pool POST is in flight. */
    readonly isCreatingPool: ReadonlySignal<boolean>;
    /** Last create-pool failure message, or null. Rendered inside the dialog. */
    readonly createPoolError: ReadonlySignal<string | null>;
    /** Open the Create Pool dialog with a fresh draft and clean submit slate. */
    openCreatePool(): void;
    /** Close the Create Pool dialog, dropping any lingering error. */
    closeCreatePool(): void;
    /** Merge form-field edits into {@link createPoolDraft}. */
    updateCreatePoolDraft(patch: Partial<CreatePoolDraft>): void;
    /**
     * POST the current draft. No-op (returns `null`) unless
     * {@link canSubmitCreatePool}. In the firm-scoped view the page's
     * firm is the target and the draft's `firmId` is ignored. On
     * success the dialog closes, a toast fires, the pool list is
     * invalidated, the upload wizard is pre-opened when the draft's
     * `initMode` is `'upload'`, and the new pool's id is returned —
     * the view is responsible for pushing the detail URL (the URL,
     * not the model, is the source of truth for the open pool; see
     * `PoolManagement.tsx`). On failure the dialog stays open with
     * {@link createPoolError} set and `null` is returned.
     */
    submitCreatePool(): Promise<string | null>;

    // --- Delete pool ---
    /**
     * Pool whose delete-confirm dialog is open, or `null`. Resolved
     * from the pool list at read time so the dialog always renders the
     * current name.
     */
    readonly pendingDeletePool: ReadonlySignal<Pool | null>;
    /** The reason the user has typed into the delete-confirm dialog. */
    readonly deletePoolReason: ReadonlySignal<string>;
    /**
     * Whether the delete is submittable: a pending pool, a trimmed
     * reason of at least {@link MIN_DELETE_POOL_REASON_LENGTH}
     * characters, and no DELETE already in flight.
     */
    readonly canConfirmDeletePool: ReadonlySignal<boolean>;
    /** True while the delete-pool DELETE is in flight. */
    readonly isDeletingPool: ReadonlySignal<boolean>;
    /** Last delete-pool failure message, or null. Rendered inside the dialog. */
    readonly deletePoolError: ReadonlySignal<string | null>;
    /**
     * Open the delete-confirm dialog for `poolId` with a blank reason.
     * No-op unless the caller holds `owner` on the pool — deletion
     * additionally requires owner access on the pool's firm, which the
     * pool payload can't distinguish; the backend rejects the rest.
     * Also a no-op for legacy pools ({@link legacyPoolIds}), which the
     * backend refuses to delete.
     */
    requestDeletePool(poolId: string): void;
    /** Close the delete-confirm dialog. Ignored while the DELETE is in flight. */
    cancelDeletePool(): void;
    /** Replace the staged deletion reason. */
    setDeletePoolReason(reason: string): void;
    /**
     * Issue the DELETE for the pending pool. No-op (returns `false`)
     * unless {@link canConfirmDeletePool}. On success the dialog
     * closes, a toast fires, the pool list is invalidated (which drops
     * the card from the list view), and `true` is returned. On failure
     * the dialog stays open with {@link deletePoolError} set and
     * `false` is returned.
     */
    confirmDeletePool(): Promise<boolean>;

    // --- Item availability (suspend / publish) ---
    /**
     * Row ids with an availability PATCH in flight. The rows already
     * reflect the requested value optimistically; the view disables the
     * toggle while its id is in this set.
     */
    readonly pendingAvailabilityRowIds: ReadonlySignal<ReadonlySet<string>>;
    /**
     * Suspend (`false`) or publish (`true`) a single pool item,
     * PATCHing immediately (not staged like quantity/price edits — the
     * toggle is an operational action, mirroring pool Pause/Resume).
     * Optimistic with rollback on failure. No-op for draft rows, reader
     * scope, and legacy pools (whose backend silently ignores
     * availability — the view hides the column for them).
     */
    setItemAvailability(rowId: string, available: boolean): void;

    // --- Pool status ---
    /** Id of the pool whose pause-confirm modal is open, or `null`. */
    readonly pendingPauseId: ReadonlySignal<string | null>;
    /** Open the pause-confirm modal for `poolId`. No-op on a paused pool or reader scope. */
    requestPausePool(poolId: string): void;
    /** Close the pause-confirm modal without changing status. */
    cancelPausePool(): void;
    /** Commit the pending pause. Safe to call when no pool is pending. */
    confirmPausePool(): void;
    /** Flip a paused pool back to active. No confirmation prompt. */
    resumePool(poolId: string): void;

    // --- Pool permissions (Permissions tab) ---
    /**
     * Firm-member permission holders for the selected pool. Empty when
     * no pool is open. Each entry is the highest rung the user holds;
     * permissions granted to groups, firms, or pseudo-subjects on the
     * server are not surfaced in this list — the UI deliberately
     * restricts to firm members only.
     */
    readonly poolPermissionHolders: ReadonlySignal<ReadonlyArray<PoolPermissionHolder>>;
    /**
     * Members of the selected pool's firm who do NOT currently hold
     * a direct permission. Drives the "Grant access" selector so the
     * user can only pick from valid firm members.
     */
    readonly poolPermissionCandidates: ReadonlySignal<ReadonlyArray<PoolPermissionCandidate>>;
    /**
     * Staged role change for the confirm dialog, or `null` when no
     * prompt is showing. Carries the target user and intended rung so
     * the dialog can render labels without re-deriving from the
     * holders list (which might shift after a refetch).
     */
    readonly pendingPermissionChange: ReadonlySignal<{
        userId: string;
        userName: string;
        role: PermissionRole;
    } | null>;
    /** True while any pool-permissions PUT call is in flight. */
    readonly permissionsMutating: ReadonlySignal<boolean>;
    /**
     * Open the confirm dialog to flip an existing holder's role. The
     * concrete PUT only fires after {@link confirmSetPoolPermissionRole}.
     */
    requestSetPoolPermissionRole(userId: string, role: PermissionRole): void;
    cancelSetPoolPermissionRole(): void;
    confirmSetPoolPermissionRole(): Promise<void>;
    /** Grant `role` to the firm-member `userId` directly (no confirm prompt). */
    addPoolPermission(userId: string, role: PermissionRole): Promise<void>;
    /** Revoke every permission rung this user holds on the current pool. */
    removePoolPermission(userId: string): Promise<void>;

    // --- Search commands ---
    setPoolSearchQuery(query: string): void;
    setPoolStatusFilter(filter: PoolStatusFilter): void;
    setSymbolSearchQuery(query: string): void;

    // --- Write commands (no-op when read-only) ---
    setAddSymbolDraft(value: string): void;
    /**
     * Stage the drafted / given symbol as a pending add. Async because
     * the duplicate check asks the backend's exact-match filter (the
     * paged items query only sees the current page); a duplicate or a
     * failed check rejects the add and surfaces a toast.
     */
    addSymbolFromDraft(): Promise<void>;
    addSymbol(symbol: string): Promise<void>;
    editRowQuantity(rowId: string, value: number): void;
    editRowLabPrice(rowId: string, value: number): void;
    clearRowQuantityEdit(rowId: string): void;
    clearRowLabPriceEdit(rowId: string): void;
    removeRow(rowId: string): void;
    restoreRow(rowId: string): void;
    resetEdits(): void;
    openSubmitReview(): void;
    closeSubmitReview(): void;
    submitChanges(): Promise<boolean>;
    clearSubmitError(): void;

    // --- Upload wizard commands ---
    openUploadWizard(): void;
    closeUploadWizard(): void;
    setUploadStep(step: 'drop' | 'review' | 'confirm' | 'done'): void;
    setUploadCategoryFilter(category: 'added' | 'updated' | 'removed' | 'errors' | 'unchanged'): void;
    setUploadSymbolFilter(query: string): void;
    setUploadMode(mode: UploadMode): void;
    selectUploadFile(file: File): Promise<void>;
    clearUploadFile(): void;
    applyUpload(): Promise<boolean>;
    downloadInventoryTemplate(): void;
    /**
     * Trigger a CSV download of the currently-selected pool's full
     * inventory from the server (not just the current page). Returns
     * once the file has been handed to the browser. No-op when no pool
     * is open or when running outside a browser.
     */
    downloadCurrentPoolCsv(): Promise<void>;
    /**
     * Trigger a CSV download of the entire filtered change-log audit
     * trail (fetched across all pages, not just the current page).
     * No-op when no pool is open, the filtered log is empty, or when
     * running outside a browser.
     */
    downloadChangeLogCsv(): Promise<void>;
    /**
     * Trigger a CSV download of the pool list as currently filtered
     * (search query + status filter). No-op when the filtered list is
     * empty or when running outside a browser.
     */
    downloadPoolListCsv(): void;

    refresh(): Promise<void>;
}
